import React from 'react';
import { fmtUSD } from '../utils/cost';

// Bake receipt panel: the backend attaches per-stage time/cost metadata to
// every completed publish (movie.render.receipt, built by buildBakeReceipt in
// backend/api/render.js). Rendered in the editor's Review tab and — for
// signed-in (allowlisted) users — on the Watch page under the player. Native
// <details> keeps it collapsed by default with zero state plumbing.
const fmtSec = (s) => {
  const v = Math.max(0, Math.round(s || 0));
  return v >= 60 ? `${Math.floor(v / 60)}m ${String(v % 60).padStart(2, '0')}s` : `${v}s`;
};
const cell = { padding: '2px 10px 2px 0', whiteSpace: 'nowrap' };
const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export default function BakeReceipt({ receipt, style }) {
  if (!receipt) return null;
  const rc = receipt;
  return (
    <details style={{ fontSize: 13, ...style }}>
      <summary style={{ cursor: 'pointer', color: 'var(--ink-soft, #9aa)' }}>
        🧾 Bake receipt — {fmtSec(rc.totalSec)} · {fmtUSD(rc.billedUsd)} fan-out + {fmtUSD(rc.includedUsd)} service (included)
      </summary>
      <div style={{ marginTop: 8 }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {(rc.stages || []).map((s) => (
              <tr key={s.key}>
                <td style={cell}>{s.label || s.key}</td>
                <td style={num}>{fmtSec(s.sec)}</td>
                <td style={num} title="Runs on the always-on service task — billed whether or not it publishes">
                  {fmtUSD(s.usd)} incl.
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(rc.jobs || []).map((j) => (
          <details key={j.jobId} style={{ marginTop: 6 }}>
            <summary style={{ cursor: 'pointer' }}>
              {j.title || j.type || j.jobId} — {j.tasks} task{j.tasks === 1 ? '' : 's'}
              {j.warmupSecAvg != null ? ` · warm-up ~${fmtSec(j.warmupSecAvg)} (max ${fmtSec(j.warmupSecMax)})` : ''}
              {' · '}{fmtSec(j.taskSeconds)} task-time · {fmtUSD(j.usd)}
            </summary>
            {j.units?.length > 0 && (
              <table style={{ borderCollapse: 'collapse', marginTop: 4, marginLeft: 14 }}>
                <thead>
                  <tr style={{ color: 'var(--ink-soft, #9aa)' }}>
                    <td style={cell}>unit</td><td style={num}>warm-up</td><td style={num}>run</td>
                    <td style={num}>download</td><td style={num}>encode</td><td style={num}>upload</td><td style={num}>cost</td>
                  </tr>
                </thead>
                <tbody>
                  {j.units.map((u, i) => (
                    <tr key={i}>
                      <td style={cell}>{u.label || `#${i + 1}`}</td>
                      <td style={num}>{u.warmupSec != null ? fmtSec(u.warmupSec) : '—'}</td>
                      <td style={num}>{fmtSec(u.runSec)}</td>
                      <td style={num}>{u.downloadSec != null ? fmtSec(u.downloadSec) : '—'}</td>
                      <td style={num}>{u.encodeSec != null ? fmtSec(u.encodeSec) : '—'}</td>
                      <td style={num}>{u.uploadSec != null ? fmtSec(u.uploadSec) : '—'}</td>
                      <td style={num}>{fmtUSD(u.usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </details>
        ))}
        <div className="cs-help" style={{ marginTop: 6 }}>
          Fan-out tasks bill per-second ({rc.jobs?.length || 0} job{rc.jobs?.length === 1 ? '' : 's'});
          “incl.” stages run on the always-on service task ({rc.serviceVcpu} vCPU / {rc.serviceGb} GB) and are
          already covered by its monthly cost.
        </div>
      </div>
    </details>
  );
}
